import React from 'react';

import {
  Cake as IconCake,
  Person as PersonIcon,
  Shield as ShieldIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Chip,
  chipClasses,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  svgIconClasses,
  Typography,
} from '@mui/material';

import { SxProp } from '@hrlogics/react-helper-types';

import { DateFormatPatterns } from '@clearverify/constants';
import { formatDate, getUserFullName } from '@clearverify/utils';

import type { EmployeeSearchMatchInfoFragment } from 'src/api';
import { formatMaskedSsnNumber } from 'src/utils/ssn';

type EmployeeSearchMatchListProps = {
  matches: EmployeeSearchMatchInfoFragment[];
  selectedSsnEncrypted?: string;
  onSelect?: (ssnEncrypted: string) => void;
};

export const EmployeeSearchMatchList: React.FC<EmployeeSearchMatchListProps> = ({
  matches,
  selectedSsnEncrypted,
  onSelect,
}) => (
  <List disablePadding sx={listStyle}>
    {matches.map(match => (
      <ListItem key={match.ssnEncrypted} disablePadding sx={listItemStyle}>
        <ListItemButton
          onClick={() => onSelect?.(match.ssnEncrypted)}
          sx={buttonStyle(match.ssnEncrypted === selectedSsnEncrypted)}
        >
          <ListItemAvatar sx={avatarContainerStyle}>
            <Avatar sx={avatarStyle}>
              <PersonIcon />
            </Avatar>
          </ListItemAvatar>
          <Box display="flex" flexDirection="column" gap={0.25} overflow="hidden">
            <Box display="flex" alignItems="center" gap={1} overflow="hidden">
              <Typography variant="body2" sx={fullNameBlockStyle}>
                {getUserFullName(match)}
              </Typography>

              {match.isPlatformUserExists && (
                <Chip
                  icon={<VerifiedUserIcon />}
                  label="CV user"
                  variant="outlined"
                  size="small"
                  sx={chipStyle}
                />
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="body3" whiteSpace="nowrap">
                <Typography variant="inherit" component="span" sx={iconContainerStyle}>
                  <IconCake />
                </Typography>{' '}
                {formatDate(match.birthDate, DateFormatPatterns.shortDateWithSlash)}
              </Typography>
              <Typography variant="body3" whiteSpace="nowrap">
                <Typography variant="inherit" component="span" sx={iconContainerStyle}>
                  <ShieldIcon />
                </Typography>{' '}
                {formatMaskedSsnNumber(match.last4OfSsn)}
              </Typography>
            </Box>
          </Box>
        </ListItemButton>
      </ListItem>
    ))}
  </List>
);

const listStyle: SxProp = theme => ({
  [theme.breakpoints.down('md')]: {
    maxHeight: 300,
    overflow: 'auto',
  },
});

const listItemStyle: SxProp = {
  '&:not(:first-of-type)': {
    mt: 1,
  },
};

const avatarContainerStyle: SxProp = {
  minWidth: 0,
};

const avatarStyle: SxProp = theme => ({
  backgroundColor: theme.palette.primary.light,
});

const buttonStyle =
  (isSelected: boolean): SxProp =>
  theme => ({
    gap: 1,
    borderStyle: 'solid',
    borderColor: isSelected ? theme.palette.primary.main : theme.palette.grey[300],
    borderRadius: 2.5,
    borderWidth: isSelected ? 1 : 1,
    py: 1,
    px: 1,
  });

const fullNameBlockStyle: SxProp = {
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
};

const chipStyle: SxProp = theme => ({
  height: 'fit-content',
  px: 0.4,
  fontSize: '0.6rem',
  [`.${svgIconClasses.root}`]: {
    margin: 0,
    color: theme.palette.primary.main,
    fontSize: '0.6rem',
  },
  [`.${chipClasses.label}`]: {
    padding: 0,
    paddingLeft: 0.25,
  },
});

const iconContainerStyle: SxProp = theme => ({
  color: theme.palette.primary.main,
  '& > *': {
    fontSize: '0.75rem',
  },
});
